import React from 'react';
import { motion } from 'framer-motion';
import {
  CreditCard, MessageCircle, ChevronRight, HelpCircle,
  Wallet, Receipt, FileText, Eye, EyeOff, History, Shield
} from 'lucide-react';
import { APP_VERSION } from '@/version';
import DomainActasCard from './DomainActasCard';
import { formatDiscountExpiry } from './dateHelpers';

const MobileLayout = ({
  debtor, tenant, portfolio, primaryColor, documentInfo, visibleFields,
  totalDebt, totalDebtWithFees, discountAmount, feesAmount, amountToPay,
  nearestMontoExpiry, nearestDiscountExpiry,
  allowPartialActaPayment, hasActaAmounts, pendingActas, canSelectActas,
  showBalance, setShowBalance, showDetail, setShowDetail,
  selectedActas, toggleActa,
  quickActions, descargoConfig,
  onPayNow, onShowChat, onShowDescargo
}) => {
  return (
    <div className="lg:hidden min-h-screen" style={{ backgroundColor: primaryColor }}>
      {/* Header */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {portfolio?.logo_url || tenant?.logo_url ? (
              <img src={portfolio?.logo_url || tenant?.logo_url} alt={tenant?.name} className="w-10 h-10 rounded-full object-contain bg-white p-1" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"><Wallet className="w-5 h-5 text-white" /></div>
            )}
            <div className="text-white">
              <p className="text-sm opacity-80">Hola,</p>
              <p className="font-semibold text-lg truncate max-w-[180px]">{debtor?.name?.split(' ')[0]}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-white/60" />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onShowChat} className="relative p-2 bg-white/20 rounded-full">
              <MessageCircle className="w-5 h-5 text-white" />
            </button>
            <button className="px-3 py-1.5 bg-white rounded-full text-sm font-medium flex items-center gap-1" style={{ color: primaryColor }}>
              <HelpCircle className="w-4 h-4" /> Ayuda
            </button>
          </div>
        </div>
      </div>

      {/* Balance Card */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mx-4 mt-2">
        <div className="bg-white rounded-3xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-600 text-sm font-medium">Deuda Total</span>
            <button onClick={() => setShowDetail(!showDetail)} data-testid="toggle-debt-detail" className="text-sm font-medium flex items-center gap-1" style={{ color: primaryColor }}>
              {showDetail ? 'Ocultar' : 'Ver detalle'} <ChevronRight className={`w-4 h-4 transition-transform ${showDetail ? 'rotate-90' : ''}`} />
            </button>
          </div>
          <div className="flex items-center gap-3 mb-1">
            <div className="text-4xl font-bold text-slate-900" data-testid="total-debt">
              {showBalance ? (<><span className="text-2xl">$</span>{totalDebtWithFees.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</>) : '$ ------'}
            </div>
            <button onClick={() => setShowBalance(!showBalance)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
              {showBalance ? <Eye className="w-5 h-5 text-slate-400" /> : <EyeOff className="w-5 h-5 text-slate-400" />}
            </button>
          </div>
          {nearestMontoExpiry && <p className="text-xs font-semibold text-amber-700 mb-4" data-testid="nearest-monto-expiry">Vence el {formatDiscountExpiry(nearestMontoExpiry)}</p>}
          {!nearestMontoExpiry && <div className="mb-4" />}

          {showDetail && (
            <div className="border-t border-slate-100 pt-4 mb-4 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {debtor?.dni && <div><span className="text-slate-500 text-xs">DNI</span><p className="font-medium text-slate-800">{debtor.dni}</p></div>}
                {debtor?.cuit && <div><span className="text-slate-500 text-xs">CUIT</span><p className="font-medium text-slate-800">{debtor.cuit}</p></div>}
                {debtor?.client_code && <div><span className="text-slate-500 text-xs">Codigo</span><p className="font-medium text-slate-800">{debtor.client_code}</p></div>}
                {debtor?.email && <div><span className="text-slate-500 text-xs">Email</span><p className="font-medium text-slate-800 break-all">{debtor.email}</p></div>}
                {debtor?.phone && <div><span className="text-slate-500 text-xs">Telefono</span><p className="font-medium text-slate-800">{debtor.phone}</p></div>}
                {debtor?.address && <div className="col-span-2"><span className="text-slate-500 text-xs">Direccion</span><p className="font-medium text-slate-800">{debtor.address}</p></div>}
                {debtor?.debt_date && <div><span className="text-slate-500 text-xs">Fecha de deuda</span><p className="font-medium text-slate-800">{debtor.debt_date}</p></div>}
                {feesAmount > 0 && <div><span className="text-slate-500 text-xs">Honorarios</span><p className="font-medium text-amber-700">${feesAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p></div>}
              </div>
              {debtor?.domains?.length > 0 && debtor.domains.some(d => d.plate) && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="debtor-domains">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-amber-800">Dominios e Infracciones</h4>
                    {allowPartialActaPayment && hasActaAmounts && pendingActas.length > 0 && (
                      <button onClick={() => toggleActa('__all__')} className="text-xs font-medium text-amber-700 hover:text-amber-900">
                        {selectedActas.length === pendingActas.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    {debtor.domains.filter(d => d.plate).map((domain) => (
                      <DomainActasCard key={domain.plate} domain={domain} validActas={domain.actas?.filter(a => a.number) || []} primaryColor={primaryColor}
                        allowPartialPayment={allowPartialActaPayment && hasActaAmounts} selectedActas={selectedActas} onToggleActa={toggleActa} />
                    ))}
                  </div>
                  {allowPartialActaPayment && hasActaAmounts && selectedActas.length > 0 && (
                    <div className="mt-3 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-teal-800 font-medium">{selectedActas.length} acta{selectedActas.length > 1 ? 's' : ''} seleccionada{selectedActas.length > 1 ? 's' : ''}</span>
                        <span className="text-lg font-bold text-teal-800">${amountToPay.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {discountAmount != null && discountAmount > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4">
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-sm text-emerald-700 font-medium">Monto con Quita</span>
                  <p className="text-xs text-emerald-600 mt-0.5">Pagando hoy, tu deuda queda en:</p>
                  {nearestDiscountExpiry && <p className="text-xs font-semibold text-amber-700 mt-1" data-testid="nearest-expiry">Vence el {formatDiscountExpiry(nearestDiscountExpiry)}</p>}
                </div>
                <span className="text-xl font-bold text-emerald-700" data-testid="discount-amount">${discountAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          )}

          {debtor?.crm_data?.saldos?.length > 0 && (
            <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4 mb-4" data-testid="crm-saldos">
              <h4 className="text-sm font-semibold text-cyan-800 mb-3">Detalle de Cuotas</h4>
              <div className="space-y-3">
                {debtor.crm_data.saldos.map((saldo) => (
                  <div key={saldo.balance_number}>
                    {saldo.installments?.length > 0 && (
                      <div className="space-y-1">
                        {saldo.installments.map((cuota) => (
                          <div key={cuota.installment_number} className="flex justify-between items-center bg-white rounded-lg px-3 py-2 text-sm">
                            <div>
                              <span className="font-medium text-slate-700">Cuota {cuota.installment_number}</span>
                              <span className="text-xs text-slate-500 ml-2">Vto: {cuota.due_date}</span>
                            </div>
                            <span className="font-bold text-slate-900">${cuota.amount?.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {debtor.crm_data.fichas?.some(f => f.pago_facil_code || f.rapipago_code) && (
                <div className="mt-3 pt-3 border-t border-cyan-200">
                  <p className="text-xs font-medium text-cyan-800 mb-1">Otros medios de pago:</p>
                  {debtor.crm_data.fichas.map((f) => (
                    <div key={f.portfolio_group_name || f.client_name} className="text-xs text-cyan-700">
                      {f.pago_facil_code && <p>Pago Facil: <span className="font-mono">{f.pago_facil_code}</span></p>}
                      {f.rapipago_code && <p>Rapipago: <span className="font-mono">{f.rapipago_code}</span></p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {feesAmount > 0 && (
            <div className="bg-slate-50 rounded-xl p-3 mb-4 text-sm">
              <div className="flex justify-between text-slate-600">
                <span>Capital</span>
                <span>${totalDebt.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-slate-600 mt-1">
                <span>Honorarios ({portfolio?.fees_percentage}%{portfolio?.fees_include_vat ? ' + IVA' : ''})</span>
                <span>${feesAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Portfolio Card */}
      {portfolio && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mx-4 mt-4">
          <div className="bg-slate-800 rounded-2xl p-4 text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              {portfolio.logo_url ? (
                <img src={portfolio.logo_url} alt={portfolio.name} className="w-12 h-12 rounded-lg object-contain bg-white p-1" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center"><Receipt className="w-6 h-6" /></div>
              )}
              <div>
                <p className="font-semibold">{portfolio.name}</p>
                <p className="text-sm text-white/60">{portfolio.portfolio_type === 'servicio' ? 'Servicio' : portfolio.portfolio_type === 'multas' ? 'Multas' : 'Efectivo'}</p>
              </div>
            </div>
            {documentInfo && (
              <button onClick={() => {}} className="flex flex-col items-center gap-1 px-4 py-2 bg-white/10 rounded-xl hover:bg-white/20 transition-colors">
                <FileText className="w-6 h-6" />
                <span className="text-xs">Ver {documentInfo.type}</span>
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* Tus Datos Card */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mx-4 mt-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-3">Tus Datos</h3>
          <div className="grid grid-cols-2 gap-3">
            {visibleFields.filter(f => !['total_debt', 'debt_date', 'plate'].includes(f.field_id)).slice(0, 6).map(field => {
              const value = debtor?.[field.field_id] || debtor?.custom_fields?.[field.field_id];
              if (!value) return null;
              return (
                <div key={field.field_id} className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500">{field.field_name}</p>
                  <p className="font-medium text-slate-900 break-words text-sm">{field.field_type === 'date' ? (formatDiscountExpiry(value) || value) : value}</p>
                </div>
              );
            })}
          </div>
          {debtor?.domains?.length > 0 && debtor.domains.some(d => d.plate) && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              {debtor.domains.filter(d => d.plate).map((d, i) => (
                <div key={d.plate} className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500">Dominio {debtor.domains.filter(dd => dd.plate).length > 1 ? i + 1 : ''}</p>
                  <p className="font-mono font-medium text-slate-900 text-sm">{d.plate}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mx-4 mt-4 mb-24">
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-4">Mas Opciones</h3>
          <div className="grid grid-cols-4 gap-4">
            {quickActions.map((action) => (
              <button key={action.id} onClick={action.onClick} className="flex flex-col items-center gap-2 relative">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: `${action.color}15` }}>
                  <action.icon className="w-7 h-7" style={{ color: action.color }} />
                </div>
                <span className="text-xs text-slate-600 text-center leading-tight">{action.label}</span>
                {action.badge && <span className="absolute top-0 right-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">{action.badge}</span>}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-6 py-3 flex justify-around items-center">
        <button className="flex flex-col items-center gap-1" style={{ color: primaryColor }}>
          <Wallet className="w-6 h-6" /><span className="text-xs font-medium">Inicio</span>
        </button>
        {descargoConfig?.enabled ? (
          <button onClick={onShowDescargo} className="flex flex-col items-center gap-1 text-slate-400" data-testid="mobile-descargo-btn">
            <Shield className="w-6 h-6" /><span className="text-xs">Descargo</span>
          </button>
        ) : (
          <button className="flex flex-col items-center gap-1 text-slate-400">
            <History className="w-6 h-6" /><span className="text-xs">Historial</span>
          </button>
        )}
        <button onClick={onPayNow} className="w-14 h-14 -mt-6 rounded-full flex items-center justify-center shadow-lg text-white" style={{ backgroundColor: primaryColor }}>
          <CreditCard className="w-7 h-7" />
        </button>
        <button onClick={onShowChat} className="flex flex-col items-center gap-1 text-slate-400">
          <MessageCircle className="w-6 h-6" /><span className="text-xs">Chat</span>
        </button>
        <button className="flex flex-col items-center gap-1 text-slate-400">
          <HelpCircle className="w-6 h-6" /><span className="text-xs">Ayuda</span>
        </button>
      </div>
      <p className="text-[10px] text-center py-1 opacity-40" style={{ color: '#fff' }}>v{APP_VERSION}</p>
    </div>
  );
};

export default MobileLayout;
